/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { EChartsOption } from "echarts";
import { distinctUntilChanged, filter, map, Subscription, switchMap, tap } from "rxjs";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ChartComponent } from "../../../shared/components/chart/chart.component";
import { DatepickerComponent } from "../../../shared/components/datepicker/datepicker.component";
import { DropdownComponent } from "../../../shared/components/dropdown/dropdown.component";
import { InputComponent } from "../../../shared/components/input/input.component";
import { timePeriodValidator } from "../../../shared/directives/validators/timeperiod.directive";
import { CloudProviderOptions } from "../../../shared/models/http/calculator/cloud-provider-options.model";
import { PlatformType } from "../../../shared/models/http/calculator/computing-platform.model";
import { OnPremiseOptions } from "../../../shared/models/http/calculator/on-premise-options.model";
import { BarChart } from "../../../shared/models/http/charts/bar-chart/bar-chart.model";
import { CalculatorForm } from "../../../shared/models/ui/calculator-form.model";
import { ChartResponse } from "../../../shared/models/ui/charts.model";
import { SelectOption } from "../../../shared/models/ui/select-option.model";
import { TimePeriodRange } from "../../../shared/models/ui/time-period-range.model";
import { CalculatorService } from "../../../shared/services/calculator.service";
import { StorageService } from "../../../shared/services/storage.service";
import { DEFAULT_PUE, PLATFORM_TYPES, STORAGE_TYPES } from "../../../shared/utils/constants";
import { getEmissionChart, getSimulationChart } from "../../../shared/utils/echarts/chart-utils";
import {
	generateCoreOptions,
	getCalculatorOptions,
	roundValueUpTo
} from "../../../shared/utils/utils";
import { CalculationKpis } from "../../../shared/models/http/calculator/calculation-kpis.model";

@Component({
	selector: "globalictcc-calculator-software-assets",
	standalone: true,
	imports: [
		ButtonComponent,
		DropdownComponent,
		DatepickerComponent,
		InputComponent,
		ChartComponent,
		ReactiveFormsModule
	],
	templateUrl: "./calculator-software-assets.component.html",
	styleUrl: "./calculator-software-assets.component.scss"
})
export class CalculatorSoftwareAssetsComponent implements OnInit, OnDestroy {
	public configurationForm = new FormGroup({
		timePeriod: new FormControl<TimePeriodRange>(
			{ start: null, end: null },
			timePeriodValidator()
		),
		computingPlatformType: new FormControl<string>("", Validators.required),
		cloudProvider: new FormControl<string>(""),
		pue: new FormControl<number | null>(DEFAULT_PUE, [Validators.min(1), Validators.max(3)]),
		computingPlatformAreas: new FormControl<string>("", Validators.required),
		computingPlatformRegions: new FormControl<string>("", Validators.required),
		cpuModel: new FormControl<string>("", Validators.required),
		cpuCores: new FormControl<number | null>(null, Validators.required),
		cpuLoad: new FormControl<number | null>(null, [
			Validators.required,
			Validators.min(0.01),
			Validators.max(1)
		]),
		storageType: new FormControl<string>("", Validators.required),
		storageSize: new FormControl<number | null>(null, [
			Validators.required,
			Validators.min(1),
			Validators.max(100000)
		]),
		gpuModel: new FormControl<string>(""),
		gpuCores: new FormControl<number | null>(null, [Validators.min(1), Validators.max(100)]),
		gpuLoad: new FormControl<number | null>(null, [Validators.min(0), Validators.max(1)]),
		memorySize: new FormControl<number | null>(null, [
			Validators.required,
			Validators.min(1),
			Validators.max(100000)
		])
	});

	public isOnPremise: boolean = true;
	public areaAndRegionOptions: CloudProviderOptions | OnPremiseOptions;

	public platformTypes: SelectOption[] = PLATFORM_TYPES;
	public storageTypes: SelectOption[] = STORAGE_TYPES;
	public cloudProviders: SelectOption[] = [];
	public areas: SelectOption[] = [];
	public regions: SelectOption[] = [];
	public availableCpuModels: SelectOption[] = [];
	public numberOfCores: SelectOption[] = [];
	public availableGpuModels: SelectOption[] = [];
	public hasCalculated: boolean = false;

	public emissionChart?: ChartResponse<BarChart>;
	public isEmissionChartLoading: boolean = false;
	public simulationChart?: ChartResponse<BarChart>;
	public isSimulationChartLoading: boolean = false;

	public emissionOverrides: EChartsOption = {
		series: {
			label: {
				show: true
			}
		}
	};

	public simulationOverrides: EChartsOption = {
		xAxis: {
			name: ""
		},
		...this.emissionOverrides
	};

	private _tenantId: string;
	private _computingPlatformTypeSub: Subscription;
	private _cloudProviderSub: Subscription;
	private _computingPlatformAreasSub: Subscription;
	private _cpuModelSub: Subscription;

	constructor(
		private _calculatorService: CalculatorService,
		private _storageService: StorageService
	) {}

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();
		this._listenFormControlChanges();
	}

	ngOnDestroy() {
		this._computingPlatformTypeSub?.unsubscribe();
		this._cloudProviderSub?.unsubscribe();
		this._computingPlatformAreasSub?.unsubscribe();
		this._cpuModelSub?.unsubscribe();
	}

	/**
	 * Resets the configuration form to its initial state.
	 * This method clears all the form fields and sets them to their default values.
	 */
	public onResetForm() {
		this.configurationForm.reset();
		this.configurationForm.controls.pue.setValue(DEFAULT_PUE);
	}

	/**
	 * Initiates the calculation process by retrieving the tenant ID and calculator options,
	 * then starts the calculator service with these parameters. If the calculator options
	 * are not available, the function returns early. Upon receiving a response from the
	 * calculator service, it updates the calculator results, CO2 value, power value, emission
	 * chart, and simulation chart.
	 */
	public onCalculate() {
		this.isEmissionChartLoading = true;
		this.isSimulationChartLoading = true;

		const calculatorOptions = getCalculatorOptions(
			this.isOnPremise,
			this.configurationForm.value as CalculatorForm
		);

		this._calculatorService
			.startCalculator(this._tenantId, calculatorOptions)
			.pipe(
				tap({
					next: calculatorResult => {
						this.hasCalculated = !!calculatorResult;

						const calculation: CalculationKpis = {
							totalCo2: roundValueUpTo(calculatorResult.co2),
							totalEnergy: roundValueUpTo(calculatorResult.power),
							equivalentTrees: roundValueUpTo(calculatorResult.treeEq),
							equivalentAuto: roundValueUpTo(calculatorResult.carEq),
							equivalentFlight: roundValueUpTo(calculatorResult.planeEq)
						};

						this._calculatorService.setCalculation(calculation);

						this.emissionChart = getEmissionChart(calculatorResult);
						this.simulationChart = getSimulationChart(calculatorResult);
					},
					error: () => {
						this.emissionChart = { chart: {} as BarChart, hasError: true };
						this.simulationChart = { chart: {} as BarChart, hasError: true };
					},
					finalize: () => {
						this.isEmissionChartLoading = false;
						this.isSimulationChartLoading = false;
					}
				})
			)
			.subscribe();
	}

	/**
	 * Listens to changes in various form controls within the configuration form and performs corresponding actions.
	 *
	 * - Listens to changes in the `computingPlatformType` control and updates the UI and fetches platform data accordingly.
	 * - Listens to changes in the `cloudProvider` control and fetches cloud provider options.
	 * - Listens to changes in the `computingPlatformAreas` control and updates the regions.
	 * - Listens to changes in the `cpuModel` control and updates the number of cores.
	 */
	private _listenFormControlChanges() {
		this._computingPlatformTypeSub =
			this.configurationForm.controls.computingPlatformType?.valueChanges
				.pipe(
					distinctUntilChanged(),
					filter(Boolean),
					tap({
						next: selectedPlatformType =>
							this._handlePlatformChangeUIUpdates(selectedPlatformType)
					}),
					switchMap(type => this._fetchPlatformData(type)),
					tap({
						next: platformData => {
							if (this.isOnPremise) {
								platformData = platformData as OnPremiseOptions;

								this._setupFormOptions(platformData);

								this.availableGpuModels = platformData.gpuModels.map(gpu => ({
									label: gpu,
									value: gpu
								}));
							} else this.cloudProviders = platformData as SelectOption[];
						}
					})
				)
				.subscribe();

		this._cloudProviderSub = this.configurationForm.controls.cloudProvider?.valueChanges
			.pipe(
				distinctUntilChanged(),
				filter(Boolean),
				switchMap(provider => this._calculatorService.getCloudProviderOptions(provider)),
				tap({
					next: cloudProviderOptions => this._setupFormOptions(cloudProviderOptions)
				})
			)
			.subscribe();

		this._cloudProviderSub =
			this.configurationForm.controls.computingPlatformAreas?.valueChanges
				.pipe(
					filter(Boolean),
					tap({
						next: area => this._updateRegions(area)
					})
				)
				.subscribe();

		this._cpuModelSub = this.configurationForm.controls.cpuModel?.valueChanges
			.pipe(
				filter(Boolean),
				tap({
					next: cpuModel => this._updateNumberOfCores(cpuModel)
				})
			)
			.subscribe();
	}

	/**
	 * Handles UI updates based on the selected platform type.
	 *
	 * @param selectedPlatformType - The type of platform selected by the user.
	 *
	 * This method updates the UI elements and form controls depending on whether
	 * the selected platform is on-premise or cloud. If the platform is on-premise,
	 * it clears the cloud providers list, resets area and region options, resets
	 * CPU and GPU options, and sets GPU validators to true. If the platform is cloud,
	 * it resets area and region options, resets CPU and GPU options, sets GPU validators
	 * to false, and resets the computing platform PUE and cloud provider form controls.
	 */
	private _handlePlatformChangeUIUpdates(selectedPlatformType: string) {
		this.isOnPremise = selectedPlatformType === PlatformType.ON_PREMISE;

		this._resetAreaAndRegionOptions();
		this._resetCpuAndGpuOptions();
		this._setGpuValidators(this.isOnPremise);
		this._setPUEValidators(this.isOnPremise);

		if (this.isOnPremise) this.cloudProviders = [];
		else {
			this.configurationForm.controls.pue.setValue(DEFAULT_PUE);
			this.configurationForm.controls.cloudProvider.reset();
		}
	}

	/**
	 * Fetches platform data based on the selected platform type.
	 *
	 * If the selected platform type is `ON_PREMISE`, it retrieves on-premise options.
	 * Otherwise, it fetches calculator form data and maps the cloud providers to an array of objects
	 * with `label` and `value` properties.
	 *
	 * @param {string} selectedPlatformType - The type of the selected platform.
	 * @returns An observable that emits the platform data.
	 */
	private _fetchPlatformData(selectedPlatformType: string) {
		return selectedPlatformType === PlatformType.ON_PREMISE
			? this._calculatorService.getOnPremiseOptions()
			: this._calculatorService.getCalculatorFormData().pipe(
					map(
						data =>
							data.cloudProviders.map(provider => ({
								label: provider,
								value: provider
							})) as SelectOption[]
					)
				);
	}

	/**
	 * Updates the regions based on the selected area.
	 *
	 * @private
	 * @param {string} selectedArea - The name of the selected area.
	 */
	private _updateRegions(selectedArea: string) {
		this.configurationForm.controls.computingPlatformRegions.reset();

		const area = this.areaAndRegionOptions.areas.find(area => area.name === selectedArea);

		this.regions = area ? area.regions.map(region => ({ label: region, value: region })) : [];
	}

	/**
	 * Updates the number of CPU cores based on the selected model.
	 *
	 * This method finds the selected CPU model from the available options,
	 * resets the CPU cores control in the configuration form, and generates
	 * the core options for the selected model.
	 *
	 * @param selectedModel - The model of the CPU selected by the user.
	 */
	private _updateNumberOfCores(selectedModel: string) {
		const selected = this.areaAndRegionOptions.cpuModels.find(
			cpu => cpu.model === selectedModel
		);

		this.configurationForm.controls.cpuCores.reset();
		this.numberOfCores = selected ? generateCoreOptions(selected.cores) : [];
	}

	/**
	 * Sets up form options for the calculator component.
	 *
	 * @param options - The cloud provider options containing areas and CPU models.
	 * @property areaAndRegionOptions - Stores the provided cloud provider options.
	 * @property areas - An array of area objects with label and value properties.
	 * @property avaiableCpuModels - An array of CPU model objects with label and value properties.
	 */
	private _setupFormOptions(options: OnPremiseOptions | CloudProviderOptions) {
		this.areaAndRegionOptions = options;

		this.configurationForm.controls.computingPlatformAreas.reset();
		this.configurationForm.controls.computingPlatformRegions.reset();
		this.regions = [];

		this.areas = options.areas.map(area => ({
			label: area.name,
			value: area.name
		}));

		this.availableCpuModels = options.cpuModels.map(cpu => ({
			label: cpu.model,
			value: cpu.model
		}));
	}

	/**
	 * Resets the CPU and GPU options in the configuration form.
	 *
	 * This method performs the following actions:
	 * - Resets the CPU model, cores, and load controls in the form.
	 * - Resets the GPU model, cores, and load controls in the form.
	 * - Clears the available CPU models array.
	 * - Clears the available GPU models array.
	 * - Clears the number of cores array.
	 */
	private _resetCpuAndGpuOptions() {
		this.configurationForm.controls.cpuModel.reset();
		this.configurationForm.controls.cpuCores.reset();
		this.configurationForm.controls.cpuLoad.reset();
		this.configurationForm.controls.gpuModel.reset();
		this.configurationForm.controls.gpuCores.reset();
		this.configurationForm.controls.gpuLoad.reset();
		this.availableCpuModels = [];
		this.availableGpuModels = [];
		this.numberOfCores = [];
	}

	/**
	 * Resets the options for computing platform areas and regions in the configuration form.
	 * This method clears the form controls for computing platform areas and regions,
	 * and also resets the internal arrays for areas and regions to empty arrays.
	 */
	private _resetAreaAndRegionOptions() {
		this.configurationForm.controls.computingPlatformAreas.reset();
		this.configurationForm.controls.computingPlatformRegions.reset();
		this.areas = [];
		this.regions = [];
	}

	/**
	 * Sets or clears the validators for GPU-related form controls based on the provided requirement flag.
	 *
	 * @param isRequired - A boolean flag indicating whether the GPU controls should be required.
	 *                      If true, the GPU controls will have the `Validators.required` validator set.
	 *                      If false, the validators for the GPU controls will be cleared.
	 */
	private _setGpuValidators(isRequired: boolean) {
		const gpuControls = [
			this.configurationForm.controls.gpuCores,
			this.configurationForm.controls.gpuModel,
			this.configurationForm.controls.gpuLoad
		];

		gpuControls.forEach(control => {
			if (isRequired) {
				control.addValidators(Validators.required);
			} else {
				control.removeValidators(Validators.required);
				control.setErrors(null);
			}
		});

		this.configurationForm.updateValueAndValidity();
	}

	/**
	 * Sets or clears the validators for the computing platform PUE control based on the provided requirement flag.
	 *
	 * @param isRequired - A boolean flag indicating whether the PUE control should be required.
	 *                      If true, the PUE control will have the `Validators.required` validator set.
	 *                      If false, the validator for the PUE control will be cleared.
	 */
	private _setPUEValidators(isRequired: boolean) {
		const pueControl = this.configurationForm.controls.pue;

		if (isRequired) {
			pueControl.addValidators(Validators.required);
		} else {
			pueControl.removeValidators(Validators.required);
			pueControl.setErrors(null);
		}

		this.configurationForm.updateValueAndValidity();
	}
}
